import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ChecklistItem {
  key: string;
  title: string;
  description: string;
  done: boolean;
  action?: { label: string; tab: string };
}

const DEFAULTS = {
  font_family: 'IBM Plex Mono',
  text_color: '#000000',
  hover_transition: 'quickflip',
  menu_position_h: 'central',
  menu_position_v: 'centro',
};

export function useOnboardingChecklist(userId: string | undefined) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [siteName, setSiteName] = useState('');

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    const [
      { data: homeImages },
      { data: aboutPage },
      { data: projects },
      { data: settings },
      { data: profile },
    ] = await Promise.all([
      supabase.from('home_images').select('id').eq('user_id', userId).limit(1),
      supabase.from('about_pages').select('bio_blocks, photo_url').eq('user_id', userId).maybeSingle(),
      supabase.from('projects').select('id, published').eq('user_id', userId),
      supabase.from('site_settings').select('logo_url, favicon_url, site_title, site_description, font_family, text_color, hover_transition, menu_position_h, menu_position_v').eq('user_id', userId).maybeSingle(),
      supabase.from('user_profiles').select('site_name').eq('user_id', userId).maybeSingle(),
    ]);

    setSiteName(profile?.site_name || '');

    // 1. Account created — always true
    const accountCreated = true;

    // 2. First photo
    const hasPhoto = (homeImages?.length ?? 0) > 0;

    // 3. About filled
    const bioBlocks = aboutPage?.bio_blocks as any[] | null;
    const aboutFilled = !!(
      (bioBlocks && bioBlocks.length > 0 && bioBlocks.some((b: any) => b.text?.trim())) ||
      aboutPage?.photo_url
    );

    // 4. Section created
    const hasSection = (projects?.length ?? 0) > 0;

    // 5. Appearance configured
    const appearanceChanged = settings ? (
      settings.font_family !== DEFAULTS.font_family ||
      settings.text_color !== DEFAULTS.text_color ||
      settings.hover_transition !== DEFAULTS.hover_transition ||
      settings.menu_position_h !== DEFAULTS.menu_position_h ||
      settings.menu_position_v !== DEFAULTS.menu_position_v
    ) : false;

    // 6. Visual identity
    const hasIdentity = !!(settings?.logo_url || settings?.favicon_url);

    // 7. Site configured
    const siteConfigured = !!(
      settings?.site_title &&
      settings.site_title !== 'CRIAAA — Portfolio' &&
      settings?.site_description &&
      settings.site_description !== 'criaaa.com — Portfolio de fotografia contemporânea'
    );

    // 8. Site published
    const hasPublished = (projects || []).some((p: any) => p.published === true);

    setItems([
      { key: 'account', title: 'Conta criada', description: 'Seu acesso está configurado.', done: accountCreated },
      { key: 'photo', title: 'Adicione sua primeira foto', description: 'Faça upload de imagens na Home para começar sua galeria.', done: hasPhoto, action: { label: 'IR PARA HOME →', tab: 'home' } },
      { key: 'about', title: 'Preencha o About', description: 'Adicione sua bio, foto e dados de contato.', done: aboutFilled, action: { label: 'IR PARA ABOUT →', tab: 'about' } },
      { key: 'section', title: 'Crie uma seção', description: 'Organize seu trabalho em Retratos, Exposições ou Galerias.', done: hasSection, action: { label: 'CRIAR SEÇÃO →', tab: 'menu' } },
      { key: 'appearance', title: 'Configure a aparência', description: 'Escolha fonte, cores e a posição do menu do seu site.', done: appearanceChanged, action: { label: 'IR PARA APARÊNCIA →', tab: 'aparencia' } },
      { key: 'identity', title: 'Adicione sua identidade visual', description: 'Faça upload do seu logo e favicon.', done: hasIdentity, action: { label: 'IR PARA APARÊNCIA →', tab: 'aparencia' } },
      { key: 'config', title: 'Configure o site', description: 'Adicione título, descrição e imagem de compartilhamento.', done: siteConfigured, action: { label: 'IR PARA CONFIGURAÇÕES →', tab: 'configuracoes' } },
      { key: 'publish', title: 'Publique o site', description: 'Quando estiver pronto, publique para o mundo ver.', done: hasPublished, action: { label: 'PUBLICAR →', tab: 'menu' } },
    ]);

    setLoading(false);
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const completedCount = items.filter(i => i.done).length;
  const totalCount = items.length || 8;
  const allDone = completedCount === totalCount && totalCount > 0;

  return { items, loading, completedCount, totalCount, allDone, siteName, refresh };
}
